import React, { useState } from "react";
import { FiUser, FiDroplet, FiTrendingUp, FiTrendingDown } from "react-icons/fi";
import { MiniBar, effColor } from "./waterHelpers";

const STATUS_STYLE = {
  Excellent: { bg: "#d1fae5", color: "#059669" },
  Normal:    { bg: "#dbeafe", color: "#2563eb" },
  High:      { bg: "#fef3c7", color: "#d97706" },
  Critical:  { bg: "#fee2e2", color: "#dc2626" },
};

const WmFlatMeteringPanel = ({ d }) => {
  const [blockFilter, setBlockFilter] = useState("All");
  const [sortBy, setSortBy]           = useState("monthlyUsage");
  const [selected, setSelected]       = useState(null);

  const blocks = ["All", "A", "B", "C", "D"];
  const filtered = d.flats
    .filter(f => blockFilter === "All" || f.block === blockFilter)
    .sort((a, b) => b[sortBy] - a[sortBy]);

  const maxUsage = Math.max(...d.flats.map(f => f.monthlyUsage));
  const selFlat = selected ? d.flats.find(f => f.flatNo === selected) : null;

  return (
    <div className="wm-flats">

      {/* ── Controls ── */}
      <div className="wf-controls">
        <div className="wf-filter-bar">
          {blocks.map(b => (
            <button
              key={b}
              className={`wf-filter-chip ${blockFilter === b ? "wf-chip-active" : ""}`}
              onClick={() => setBlockFilter(b)}
            >
              {b === "All" ? "All Blocks" : `Block ${b}`}
            </button>
          ))}
        </div>
        <div className="wf-sort-row">
          <span className="wf-sort-label">Sort:</span>
          {[
            { key: "monthlyUsage",  label: "Usage" },
            { key: "efficiencyScore", label: "Score" },
            { key: "dailyAvg",     label: "Daily" },
          ].map(s => (
            <button key={s.key} className={`wf-sort-btn ${sortBy === s.key ? "wf-sort-active" : ""}`} onClick={() => setSortBy(s.key)}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="wf-layout">
        {/* ── Flat List ── */}
        <div className="wf-list">
          <div className="wf-list-header">
            <span>Flat / Block</span><span>Monthly</span><span>vs Prev</span><span>Score</span><span>Bar</span>
          </div>
          {filtered.map(flat => {
            const ss = STATUS_STYLE[flat.status] || STATUS_STYLE.Normal;
            const trend = flat.monthlyUsage > flat.prevMonthUsage;
            return (
              <div
                key={flat.flatNo}
                className={`wf-flat-row ${selected === flat.flatNo ? "wf-flat-active" : ""}`}
                onClick={() => setSelected(selected === flat.flatNo ? null : flat.flatNo)}
              >
                <div className="wf-flat-id">
                  <strong>{flat.flatNo}</strong>
                  <span className="wf-flat-sub">
                    <FiUser size={10} /> {flat.occupants}
                  </span>
                  <span className="wf-flat-status" style={{ background: ss.bg, color: ss.color }}>{flat.status}</span>
                </div>
                <span className="wf-flat-usage">{(flat.monthlyUsage / 1000).toFixed(1)} KL</span>
                <span className="wf-flat-trend" style={{ color: trend ? "#ef4444" : "#10b981" }}>
                  {trend ? <FiTrendingUp size={13} /> : <FiTrendingDown size={13} />}
                  {Math.abs(((flat.monthlyUsage - flat.prevMonthUsage) / flat.prevMonthUsage) * 100).toFixed(0)}%
                </span>
                <span className="wf-flat-score" style={{ color: effColor(flat.efficiencyScore) }}>
                  {flat.efficiencyScore}
                </span>
                <div style={{ width: 80 }}>
                  <MiniBar value={flat.monthlyUsage} max={maxUsage} color={flat.status === "Critical" ? "#ef4444" : flat.status === "High" ? "#f59e0b" : "#3b82f6"} height={6} />
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Detail Panel ── */}
        <div className="wf-detail">
          {selFlat ? (
            <>
              <div className="wf-detail-title">{selFlat.flatNo}</div>
              <div className="wf-detail-sub">Block {selFlat.block} · Floor {selFlat.floor}</div>

              <div className="wf-detail-score">
                <div className="wf-ds-num" style={{ color: effColor(selFlat.efficiencyScore) }}>{selFlat.efficiencyScore}</div>
                <div className="wf-ds-label">Efficiency Score</div>
                <MiniBar value={selFlat.efficiencyScore} color={effColor(selFlat.efficiencyScore)} height={8} />
              </div>

              <div className="wf-detail-grid">
                {[
                  ["Occupants",       selFlat.occupants],
                  ["Monthly Usage",   `${(selFlat.monthlyUsage/1000).toFixed(2)} KL`],
                  ["Prev Month",      `${(selFlat.prevMonthUsage/1000).toFixed(2)} KL`],
                  ["Change",          `${((selFlat.monthlyUsage - selFlat.prevMonthUsage)/selFlat.prevMonthUsage * 100).toFixed(1)}%`],
                  ["Daily Average",   `${selFlat.dailyAvg} L`],
                  ["Per Capita/Day",  `${Math.round(selFlat.dailyAvg / selFlat.occupants)} L`],
                  ["Status",          selFlat.status],
                ].map(([label, val]) => (
                  <div key={label} className="wf-dg-item">
                    <span className="wf-dg-label">{label}</span>
                    <span className="wf-dg-val">{val}</span>
                  </div>
                ))}
              </div>

              {(selFlat.status === "Critical" || selFlat.status === "High") && (
                <div className="wf-detail-alert">
                  ⚠ Usage significantly above average for this flat size. Consider inspection or notice to resident.
                </div>
              )}
            </>
          ) : (
            <div className="wf-empty">
              <FiDroplet size={40} color="#cbd5e1" />
              <p>Select a flat to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WmFlatMeteringPanel;
